import { Icon } from '@iconify/react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useExportAnalyticsForm } from '../hooks/Conversation/useExportAnalyticsForm.hook';
import { useModalStore } from '../store/modal.store';
import { Button } from './Shared/Button/Button';
import { DatePicker } from './Shared/DatePicker/DatePicker';
import { RadioGroup, RadioGroupItem } from './Shared/RadioGroup/RadioGroup';
import { Separator } from './Shared/Separator/Separator';
import { Switch } from './Shared/Switch/Switch';
import { ExportMode, Extension } from '@/Domain/api/Export/export.dto';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';

const translationKeyByExportMode: Record<ExportMode, string> = {
  [ExportMode.CONVERSATIONS]: 'conversations',
  [ExportMode.DATA_GATHERING]: 'data_gathering',
  [ExportMode.LINK_TRACKING]: 'link_tracking',
  [ExportMode.TRIAGE_DATA]: 'triage_data',
};

export const EXPORT_ANALYTICS_DIALOG = 'EXPORT_DIALOG';
export function exportAnalyticsDialogName(exportMode: ExportMode) {
  return `${EXPORT_ANALYTICS_DIALOG}_${exportMode}`;
}

export const ExportAnalyticsDialog = ({ exportMode }: { exportMode: ExportMode }) => {
  const { t } = useTranslation(['inbox']);
  const { isOpen, closeModal } = useModalStore();
  const translationKey = translationKeyByExportMode[exportMode];

  const { form, handleSubmit } = useExportAnalyticsForm(exportMode);

  return (
    <Dialog open={isOpen(exportAnalyticsDialogName(exportMode))} onOpenChange={closeModal}>
      <DialogContent
        className="w-lg"
        data-testid={`export-analytics-${translationKey}-dialog-content`}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} autoComplete="off" className="space-y-4">
            <DialogHeader>
              <DialogTitle>{t(`export.${translationKey}.title`)}</DialogTitle>
              <DialogDescription>{t(`export.${translationKey}.description`)}</DialogDescription>
            </DialogHeader>
            <Separator />
            <FormField
              name="start_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('export.form.start_date')}</FormLabel>
                  <FormControl>
                    <div data-testid="start_date">
                      <DatePicker {...field} id="start_date" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('export.form.end_date')}</FormLabel>
                  <FormControl>
                    <div data-testid="end_date">
                      <DatePicker {...field} id="end_date" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="extension"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="grid grid-cols-2 gap-3 items-center"
                    >
                      <RadioGroupItem
                        id={Extension.CSV}
                        value={Extension.CSV}
                        className="peer sr-only"
                      />
                      <label
                        htmlFor={Extension.CSV}
                        className={classNames(
                          'p-4 border border-border rounded-lg flex items-center justify-between  cursor-pointer',
                          {
                            '!border-foreground': field.value === Extension.CSV,
                          },
                        )}
                      >
                        <div className="flex items-center gap-3 text-sm">
                          {t('export.extensions.csv')}
                        </div>
                        {field.value === Extension.CSV ? (
                          <Icon icon="mingcute:radiobox-fill" />
                        ) : (
                          <Icon icon="mingcute:radiobox-line" className="text-foreground-subtle" />
                        )}
                      </label>

                      <RadioGroupItem
                        id={Extension.XLSX}
                        value={Extension.XLSX}
                        className="peer sr-only"
                      />
                      <label
                        htmlFor={Extension.XLSX}
                        className={classNames(
                          'p-4 border border-border rounded-lg flex items-center justify-between  cursor-pointer',
                          {
                            '!border-foreground': field.value === Extension.XLSX,
                          },
                        )}
                      >
                        {t('export.extensions.xlsx')}
                        {field.value === Extension.XLSX ? (
                          <Icon icon="mingcute:radiobox-fill" />
                        ) : (
                          <Icon icon="mingcute:radiobox-line" className="text-foreground-subtle" />
                        )}
                      </label>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Separator />

            <DialogFooter className="flex flex-row !justify-between items-center">
              <FormField
                name={`send_email_notification`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('export.send_email_notification')}</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(check) => field.onChange(check)}
                        className="mt-[5px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex gap-2">
                <Button variant="secondary" onClick={closeModal} type="button">
                  {t('cancel')}
                </Button>

                <Button
                  variant="dark"
                  type="submit"
                  data-testid="export-analytics-dialog-submit-button"
                >
                  {t('export.save')}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
